import type { PushConfigDto } from '@rw/shared';
import { api } from './api';

export type PushState =
  /** Browser has no Web Push (or no service worker). */
  | 'unsupported'
  /** iPhone/iPad Safari tab: push only works after "Add to Home Screen". */
  | 'needs-install'
  /** Server has no VAPID keys. */
  | 'disabled'
  | 'denied'
  | 'off'
  | 'on';

const isIos = () => /iPad|iPhone|iPod/.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
const isStandalone = () =>
  window.matchMedia?.('(display-mode: standalone)').matches || (navigator as Navigator & { standalone?: boolean }).standalone === true;

export function pushSupported() {
  return 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
}

let configPromise: Promise<PushConfigDto> | null = null;
function pushConfig() {
  configPromise ??= api<PushConfigDto>('/push/config').catch((e) => {
    configPromise = null;
    throw e;
  });
  return configPromise;
}

/** The active service worker (it never becomes ready in dev, where none is registered). */
async function registration() {
  const timeout = new Promise<never>((_, reject) => setTimeout(() => reject(new Error('Service worker not ready')), 10_000));
  return Promise.race([navigator.serviceWorker.ready, timeout]);
}

/**
 * The existing subscription, if any. Reads the registration directly: right after a cold start
 * (or while a new version installs) `serviceWorker.ready` can take a while, which used to make
 * an enabled device look switched off.
 */
export async function currentSubscription(): Promise<PushSubscription | null> {
  if (!pushSupported()) return null;
  const reg = (await navigator.serviceWorker.getRegistration()) ?? (await registration());
  return reg.pushManager.getSubscription();
}

export async function pushState(): Promise<PushState> {
  if (!pushSupported()) return isIos() && !isStandalone() ? 'needs-install' : 'unsupported';
  if (Notification.permission === 'denied') return 'denied';
  // A subscribed device is on — no need to reach the server (offline, slow start).
  if (Notification.permission === 'granted' && (await currentSubscription().catch(() => null))) return 'on';
  const { publicKey } = await pushConfig();
  if (!publicKey) return 'disabled';
  return 'off';
}

function keyBytes(base64url: string) {
  const b64 = (base64url + '='.repeat((4 - (base64url.length % 4)) % 4)).replace(/-/g, '+').replace(/_/g, '/');
  return Uint8Array.from(atob(b64), (c) => c.charCodeAt(0));
}

function withTimeout<T>(p: Promise<T>, ms: number, message: string): Promise<T> {
  return Promise.race([p, new Promise<never>((_, reject) => setTimeout(() => reject(new Error(message)), ms))]);
}

/**
 * Ask permission (must run from a tap), subscribe this device and register it with the server.
 * `onAsking` fires while the browser/OS permission prompt is open (it can wait on the user indefinitely).
 */
export async function enablePush(onAsking?: () => void): Promise<PushState> {
  if (!pushSupported()) return pushState();
  if (Notification.permission === 'default') onAsking?.();
  const permission = await Notification.requestPermission();
  if (permission !== 'granted') return permission === 'denied' ? 'denied' : 'off';
  const { publicKey } = await pushConfig();
  if (!publicKey) return 'disabled';
  const reg = await registration();
  let sub = await reg.pushManager.getSubscription();
  // A subscription made with an old server key can't receive our pushes.
  if (sub && sub.options.applicationServerKey) {
    const current = new Uint8Array(sub.options.applicationServerKey);
    const wanted = keyBytes(publicKey);
    if (current.length !== wanted.length || current.some((b, i) => b !== wanted[i])) {
      await sub.unsubscribe();
      sub = null;
    }
  }
  sub ??= await withTimeout(
    reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: keyBytes(publicKey) }),
    30_000,
    'The push service didn’t answer. Check your connection and try again.',
  );
  await api('/push/subscriptions', { method: 'POST', body: sub.toJSON() });
  return 'on';
}

/** Stop push on this device (keeps the browser permission). */
export async function disablePush(): Promise<PushState> {
  const sub = await currentSubscription();
  if (sub) {
    await api('/push/unsubscribe', { method: 'POST', body: { endpoint: sub.endpoint } }).catch(() => undefined);
    await sub.unsubscribe();
  }
  return 'off';
}

/** On app start: re-register an existing subscription (it may belong to another account or have been rotated). */
export async function syncPushSubscription() {
  try {
    if (!pushSupported() || Notification.permission !== 'granted') return;
    const sub = await currentSubscription();
    if (sub) await api('/push/subscriptions', { method: 'POST', body: sub.toJSON() });
  } catch {
    /* best effort */
  }
}

/**
 * On sign-out: the server stops sending the old account's notifications to this device, but the
 * device keeps its subscription, so push is still on after the next sign-in (syncPushSubscription
 * attaches it to whoever signs in). Turning it off is only done from the Notifications switch.
 */
export async function detachPushOnLogout() {
  try {
    const sub = await currentSubscription();
    if (!sub) return;
    await api('/push/unsubscribe', { method: 'POST', body: { endpoint: sub.endpoint }, timeoutMs: 3000 }).catch(() => undefined);
  } catch {
    /* best effort */
  }
}
